/**
 * User Service for V2 Services
 */

import { FirestoreService } from '../db/FirestoreService';
import { User, UserProfile, UserStats } from '../models/User.model';

export class UserService {
  private firestore: FirestoreService;
  private readonly COLLECTION = 'users';

  constructor() {
    this.firestore = FirestoreService.getInstance();
  }

  async createUser(userData: UserProfile): Promise<User> {
    const user = new User(userData);
    const docRef = await this.firestore.addDocument(this.COLLECTION, user.toFirestore());
    user.uid = docRef.id;
    return user;
  }

  async getUser(uid: string): Promise<User | null> {
    const doc = await this.firestore.getDocument(this.COLLECTION, uid);
    return doc ? User.fromFirestore({ id: uid, ...doc }) : null;
  }

  async updateUser(uid: string, updates: Partial<UserProfile>): Promise<void> {
    await this.firestore.updateDocument(this.COLLECTION, uid, updates);
  }

  async deleteUser(uid: string): Promise<void> {
    await this.firestore.deleteDocument(this.COLLECTION, uid);
  }

  async getAllUsers(limit?: number): Promise<User[]> {
    const docs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [],
      { field: 'createdAt', direction: 'desc' },
      limit
    );
    return docs.map(doc => User.fromFirestore(doc));
  }

  async getUsersByRole(isAdmin: boolean): Promise<User[]> {
    const docs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [{ field: 'isAdmin', operator: '==', value: isAdmin }]
    );
    return docs.map(doc => User.fromFirestore(doc));
  }

  async searchUsers(searchTerm: string): Promise<User[]> {
    const emailDocs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [{ field: 'email', operator: '>=', value: searchTerm }]
    );

    const nameDocs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [{ field: 'displayName', operator: '>=', value: searchTerm }]
    );

    const allDocs = [...emailDocs, ...nameDocs];
    const uniqueDocs = allDocs.filter((doc, index, self) =>
      index === self.findIndex(d => d.uid === doc.uid)
    );

    return uniqueDocs.map(doc => User.fromFirestore(doc));
  }

  async updateUserStats(uid: string, stats: Partial<UserStats>): Promise<void> {
    await this.firestore.updateDocument(this.COLLECTION, uid, { stats });
  }

  async updateLastLogin(uid: string): Promise<void> {
    await this.firestore.updateDocument(this.COLLECTION, uid, {
      lastLoginAt: new Date(),
      lastSeenAt: new Date()
    });
  }

  async updateLastSeen(uid: string): Promise<void> {
    await this.firestore.updateDocument(this.COLLECTION, uid, {
      lastSeenAt: new Date()
    });
  }

  async getUsersWithRecentActivity(days: number = 30): Promise<User[]> {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    const docs = await this.firestore.queryDocuments(
      this.COLLECTION,
      [{ field: 'lastSeenAt', operator: '>=', value: cutoffDate }],
      { field: 'lastSeenAt', direction: 'desc' }
    );
    return docs.map(doc => User.fromFirestore(doc));
  }

  subscribeToUsers(callback: (users: User[]) => void): () => void {
    return this.firestore.subscribeToCollection(
      this.COLLECTION,
      (docs) => {
        const users = docs.map(doc => User.fromFirestore(doc));
        callback(users);
      },
      [],
      { field: 'createdAt', direction: 'desc' }
    );
  }

  subscribeToUser(uid: string, callback: (user: User | null) => void): () => void {
    return this.firestore.subscribeToDocument(
      this.COLLECTION,
      uid,
      (doc) => {
        const user = doc ? User.fromFirestore({ id: uid, ...doc }) : null;
        callback(user);
      }
    );
  }
}